import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Code, FileText, Lock, Star, AlertTriangle, Info } from 'lucide-react';
import { ScanFinding } from '../types/scan.ts';
import UpgradeButton from './UpgradeButton.tsx';

// Lazy load Monaco Editor
const CodeEditor = React.lazy(() => 
  import('@monaco-editor/react').then(module => ({ 
    default: module.default 
  }))
);

interface CodeDrawerProps {
  isOpen: boolean;
  fileName: string | null;
  findings: ScanFinding[];
  isProUser: boolean;
  onClose: () => void;
  className?: string;
  activeFinding?: ScanFinding;
}

interface HighlightInfo {
  line: number;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  finding: ScanFinding;
}

// Severity color mapping for Monaco decorations
const SEVERITY_COLORS = {
  critical: '#ef4444', // Red
  high: '#ef4444',     // Red
  medium: '#f97316',   // Orange
  low: '#eab308',      // Yellow
  info: '#6b7280',     // Gray
};

const SEVERITY_CLASS_MAP = {
  critical: 'bg-red-100 border-red-500 text-red-900',
  high: 'bg-red-100 border-red-500 text-red-900',
  medium: 'bg-orange-100 border-orange-500 text-orange-900',
  low: 'bg-yellow-100 border-yellow-500 text-yellow-900',
  info: 'bg-gray-100 border-gray-500 text-gray-900',
};

// Mock file content fetcher (in real app this would fetch from API)
const fetchFileContent = async (fileName: string): Promise<string> => {
  console.log('[CodeDrawer] Fetching content for:', fileName);
  
  // Mock content based on file type for demo
  if (fileName.includes('auth.js')) {
    return `// ${fileName}
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://example.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...';

export const supabase = createClient(supabaseUrl, supabaseKey);

// Hardcoded credentials - security issue (line 8)
const adminPassword = 'admin123';
const apiKey = 'sk-abcd1234567890abcdef1234567890abcdef12';

export async function authenticateUser(username: string, password: string) {
  // Log user data - privacy issue (line 12)
  console.log('User login attempt:', { username, password, timestamp: new Date() });
  
  if (username === 'admin' && password === adminPassword) {
    return { success: true, token: apiKey };
  }
  
  return { success: false };
}

// Missing RLS policy check - compliance issue (line 21)
export async function getUserData(userId: string) {
  const { data } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();
    
  return data;
}`;
  }
  
  return `// ${fileName}
// File content would be loaded here
console.log('Sample file content for', '${fileName}');

// This is a sample file with mock content
// In a real implementation, this would fetch the actual file content
// from your repository or storage system

function exampleFunction() {
  return 'Hello from ${fileName}';
}`;
};

export const CodeDrawer: React.FC<CodeDrawerProps> = ({
  isOpen,
  fileName,
  findings,
  isProUser,
  onClose,
  className = '',
  activeFinding
}) => {
  const [fileContent, setFileContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentIssueIndex, setCurrentIssueIndex] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const editorRef = useRef<any>(null);

  console.log('[CodeDrawer] Rendered with:', {
    isOpen,
    fileName,
    findingsCount: findings.length,
    isProUser,
    currentIssueIndex
  });

  // Process findings to extract line highlights
  const highlights = useMemo<HighlightInfo[]>(() => {
    if (!findings.length) return [];
    
    return findings
      .filter(finding => finding.line && finding.line > 0)
      .map(finding => ({
        line: finding.line,
        severity: finding.severity as any,
        finding
      }))
      .sort((a, b) => a.line - b.line);
  }, [findings]);

  const currentHighlight = highlights[currentIssueIndex];

  // Load file content when fileName changes
  useEffect(() => {
    if (!fileName || !isOpen) {
      setFileContent('');
      return;
    }

    console.log('[CodeDrawer] Loading file content for:', fileName);
    setIsLoading(true);
    
    fetchFileContent(fileName)
      .then(content => {
        setFileContent(content);
        setCurrentIssueIndex(0);
      })
      .catch(error => {
        console.error('[CodeDrawer] Failed to load file:', error);
        setFileContent('// Failed to load file content');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [fileName, isOpen]);

  // Navigate to specific line in editor
  const navigateToLine = useCallback((line: number) => {
    if (editorRef.current) {
      editorRef.current.revealLineInCenter(line);
      editorRef.current.setPosition({ lineNumber: line, column: 1 });
      editorRef.current.focus();
    }
  }, []);

  // Navigate to current issue
  useEffect(() => {
    if (currentHighlight && editorRef.current) {
      navigateToLine(currentHighlight.line);
    }
  }, [currentHighlight, navigateToLine]);

  // Handle Monaco editor mount
  const handleEditorDidMount = useCallback((editor: any, monaco: any) => {
    console.log('[CodeDrawer] Monaco editor mounted');
    editorRef.current = editor;

    // Configure editor
    editor.updateOptions({
      readOnly: true,
      minimap: { enabled: false },
      lineNumbers: 'on',
      scrollBeyondLastLine: false,
      automaticLayout: true,
    });

    // Add decorations for highlighted lines
    if (highlights.length > 0) {
      const decorations = highlights.map(highlight => ({
        range: new monaco.Range(highlight.line, 1, highlight.line, 1),
        options: {
          isWholeLine: true,
          className: `line-highlight-${highlight.severity}`,
          glyphMarginClassName: `glyph-${highlight.severity}`,
        }
      }));

      editor.deltaDecorations([], decorations);
    }

    // Navigate to first issue if available
    if (currentHighlight) {
      setTimeout(() => navigateToLine(currentHighlight.line), 100);
    }
  }, [highlights, currentHighlight, navigateToLine]);

  // Navigation handlers
  const handlePrevIssue = () => {
    if (highlights.length === 0) return;
    const newIndex = currentIssueIndex > 0 ? currentIssueIndex - 1 : highlights.length - 1;
    setCurrentIssueIndex(newIndex);
  };

  const handleNextIssue = () => {
    if (highlights.length === 0) return;
    const newIndex = currentIssueIndex < highlights.length - 1 ? currentIssueIndex + 1 : 0;
    setCurrentIssueIndex(newIndex);
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Don't render if not open or no file
  if (!isOpen || !fileName) {
    return null;
  }

  // Collapsed state - show just a tab
  if (isCollapsed) {
    return (
      <div className={`fixed bottom-0 left-0 right-0 z-40 ${className}`}>
        <div className="bg-gray-800 border-t border-gray-700 px-4 py-2">
          <button
            onClick={() => setIsCollapsed(false)}
            className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            <FileText className="w-4 h-4" />
            <span className="font-mono">{fileName}</span>
            {highlights.length > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs rounded-full">
                {highlights.length}
              </span>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* CSS for line highlighting */}
      <style>{`
        .line-highlight-critical {
          background-color: rgba(239, 68, 68, 0.1) !important;
          border-left: 3px solid #ef4444 !important;
        }
        .line-highlight-high {
          background-color: rgba(239, 68, 68, 0.1) !important;
          border-left: 3px solid #ef4444 !important;
        }
        .line-highlight-medium {
          background-color: rgba(249, 115, 22, 0.1) !important;
          border-left: 3px solid #f97316 !important;
        }
        .line-highlight-low {
          background-color: rgba(234, 179, 8, 0.1) !important;
          border-left: 3px solid #eab308 !important;
        }
        .line-highlight-info {
          background-color: rgba(107, 114, 128, 0.1) !important;
          border-left: 3px solid #6b7280 !important;
        }
        .glyph-critical::before {
          content: '⚠️';
        }
        .glyph-high::before {
          content: '🔴';
        }
        .glyph-medium::before {
          content: '🟠';
        }
        .glyph-low::before {
          content: '🟡';
        }
        .glyph-info::before {
          content: 'ℹ️';
        }
      `}</style>
      
      <div className={`fixed bottom-0 left-0 right-0 z-40 bg-gray-900 border-t border-gray-700 ${className}`}>
        <div className="h-96 flex flex-col md:h-96">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-400" />
              <h3 className="font-mono text-sm text-white">{fileName}</h3>
              {highlights.length > 0 && (
                <span className="text-xs text-gray-400">
                  {highlights.length} issue{highlights.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {/* Issue Navigation */}
              {highlights.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">
                    {currentIssueIndex + 1} of {highlights.length}
                  </span>
                  <button
                    onClick={handlePrevIssue}
                    className="p-1 text-gray-400 hover:text-white transition-colors"
                    title="Previous issue"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleNextIssue}
                    className="p-1 text-gray-400 hover:text-white transition-colors"
                    title="Next issue"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {/* Collapse/Close buttons */}
              <button
                onClick={() => setIsCollapsed(true)}
                className="p-1 text-gray-400 hover:text-white transition-colors"
                title="Minimize"
              >
                <Code className="w-4 h-4" />
              </button>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-white transition-colors"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex">
            {/* Code Editor */}
            <div className="flex-1 relative">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-400">Loading file...</div>
                </div>
              ) : (
                <React.Suspense fallback={
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-400">Loading editor...</div>
                  </div>
                }>
                  <CodeEditor
                    value={fileContent}
                    language={getLanguageFromFileName(fileName)}
                    theme="vs-dark"
                    onMount={handleEditorDidMount}
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                      fontSize: 14,
                      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    }}
                  />
                </React.Suspense>
              )}
            </div>

            {/* Issue Details Panel */}
            {currentHighlight && (
              <div className="w-80 border-l border-gray-700 bg-gray-800 p-4 overflow-y-auto">
                <IssueTooltip
                  finding={currentHighlight.finding}
                  isProUser={isProUser}
                  line={currentHighlight.line}
                />
              </div>
            )}
          </div>
        </div>

        {/* Mobile: Full screen mode */}
        <div className="md:hidden fixed inset-0 bg-gray-900 z-50" style={{ display: isOpen ? 'block' : 'none' }}>
          <div className="h-full flex flex-col">
            {/* Mobile Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-400" />
                <h3 className="font-mono text-sm text-white truncate">{fileName}</h3>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Mobile Content */}
            <div className="flex-1">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-400">Loading file...</div>
                </div>
              ) : (
                <React.Suspense fallback={
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-400">Loading editor...</div>
                  </div>
                }>
                  <CodeEditor
                    value={fileContent}
                    language={getLanguageFromFileName(fileName)}
                    theme="vs-dark"
                    onMount={handleEditorDidMount}
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                      fontSize: 12,
                      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    }}
                  />
                </React.Suspense>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Issue tooltip component
const IssueTooltip: React.FC<{
  finding: ScanFinding;
  isProUser: boolean;
  line: number;
}> = ({ finding, isProUser, line }) => {
  const isPremium = finding.is_premium;
  const showUpgrade = isPremium && !isProUser;

  if (showUpgrade) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-yellow-500" />
          <Star className="w-4 h-4 text-yellow-500" />
        </div>
        <h4 className="font-semibold text-white">Premium Security Insight</h4>
        <div className="text-sm text-gray-300 blur-sm">
          {finding.message.substring(0, 50)}...
        </div>
        <p className="text-xs text-gray-400">
          Upgrade to view detailed analysis and fix recommendations
        </p>
        <UpgradeButton
          label="Upgrade to Pro"
          className="text-xs bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1.5 rounded"
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className={`w-4 h-4 ${
          finding.severity === 'high' ? 'text-red-500' :
          finding.severity === 'medium' ? 'text-orange-500' :
          finding.severity === 'low' ? 'text-yellow-500' :
          'text-gray-500'
        }`} />
        <span className={`text-xs font-medium px-2 py-1 rounded border ${
          SEVERITY_CLASS_MAP[finding.severity as keyof typeof SEVERITY_CLASS_MAP] || 'bg-gray-100 border-gray-500 text-gray-900'
        }`}>
          {finding.severity?.toUpperCase()}
        </span>
      </div>

      <div>
        <h4 className="font-semibold text-white mb-1">
          Line {line}: {finding.issue_type || finding.type}
        </h4>
        <p className="text-sm text-gray-300 mb-3">
          {finding.message}
        </p>
      </div>

      {finding.recommendation && (
        <div className="p-3 bg-blue-900/30 border border-blue-700 rounded">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-400 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-blue-300 mb-1">Recommendation</p>
              <p className="text-xs text-blue-200">{finding.recommendation}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to determine language from file name
const getLanguageFromFileName = (fileName: string): string => {
  const ext = fileName.toLowerCase().split('.').pop();
  
  switch (ext) {
    case 'js':
    case 'jsx':
      return 'javascript';
    case 'ts':
    case 'tsx':
      return 'typescript';
    case 'html':
      return 'html';
    case 'css':
      return 'css';
    case 'json':
      return 'json';
    case 'md':
      return 'markdown';
    case 'yml':
    case 'yaml':
      return 'yaml';
    case 'env':
      return 'plaintext';
    default:
      return 'plaintext';
  }
};

export default CodeDrawer;
